import type { IGetModelsComparisonReq, IModelComparisonRow } from "@/lib/backend/models.generated.types";
import { niceNumber } from "@/lib/Util";

export type ModelsStagingSortField =
    | "plant_title"
    | "line_title"
    | "material_title"
    | "process_num"
    | "active_slope"
    | "pending_slope"
    | "norm_slope"
    | "old_vs_new_diff_pct"
    | "old_vs_norm_diff_pct";

export type ModelsStagingSortDirection = "asc" | "desc";

export type ModelsStagingViewRow = IModelComparisonRow & {
    is_selected: boolean;
};

export type ModelsStagingFilterState = {
    plant_uuid: string;
    line_external_id: string;
    material_external_id: string;
    process_num: string;
    min_diff_pct: string;
    pending_only: boolean;
};

export const DEFAULT_MODELS_STAGING_FILTERS: ModelsStagingFilterState = {
    plant_uuid: "",
    line_external_id: "",
    material_external_id: "",
    process_num: "",
    min_diff_pct: "",
    pending_only: false,
};

export function isModelsStagingRowSelectable(row: IModelComparisonRow): boolean {
    return row.pending_slope !== null;
}

export function mapApiRowToViewModel(
    row: IModelComparisonRow,
    selectedModelCodes: ReadonlySet<string>
): ModelsStagingViewRow {
    return {
        ...row,
        is_selected: selectedModelCodes.has(row.model_code),
    };
}

export function clampPageIndex(pageIndex: number, totalPages: number): number | null {
    if (totalPages === 0) {
        return pageIndex === 0 ? null : 0;
    }
    if (pageIndex >= totalPages) {
        return totalPages - 1;
    }
    return null;
}

export function formatSlope(value: number | null): string {
    if (value === null) {
        return "—";
    }
    return String(value);
}

export function formatDiffPct(value: number | null): string {
    if (value === null) {
        return "—";
    }
    return `${niceNumber(value, 2, 2)}%`;
}

const DIFF_NEUTRAL_THRESHOLD_PCT = 1;

/** Lower slope is better (less time per unit). */
export function getDiffVariant(pct: number | null): string {
    if (pct === null) {
        return "";
    }
    if (Math.abs(pct) < DIFF_NEUTRAL_THRESHOLD_PCT) {
        return "variant-neutral";
    }
    return pct < 0 ? "variant-success" : "variant-danger";
}

export function buildPlantIdLookup(rows: IModelComparisonRow[]): Map<string, number> {
    const lookup = new Map<string, number>();
    for (const row of rows) {
        if (row.plant_uuid !== "" && row.plant_id > 0) {
            lookup.set(row.plant_uuid, row.plant_id);
        }
    }
    return lookup;
}

export function buildModelsComparisonReq(
    activeFilters: ModelsStagingFilterState,
    sortField: ModelsStagingSortField,
    sortDirection: ModelsStagingSortDirection,
    page: number,
    pageSize: number
): IGetModelsComparisonReq {
    const req: IGetModelsComparisonReq = {
        page,
        page_size: pageSize,
        sort: sortField,
        sort_dir: sortDirection,
    };

    if (activeFilters.plant_uuid !== "") {
        req.plant_uuid = activeFilters.plant_uuid;
    }

    if (activeFilters.line_external_id !== "") {
        req.line_external_id = activeFilters.line_external_id;
    }

    if (activeFilters.material_external_id !== "") {
        req.material_external_id = activeFilters.material_external_id;
    }

    if (activeFilters.process_num !== "") {
        req.process_num = activeFilters.process_num;
    }

    const minDiff = Number(activeFilters.min_diff_pct);
    if (activeFilters.min_diff_pct !== "" && !Number.isNaN(minDiff)) {
        req.min_diff_pct = minDiff;
    }

    if (activeFilters.pending_only) {
        req.pending_only = true;
    }

    return req;
}

function csvNumber(value: number | null): string {
    if (value === null) {
        return "";
    }
    return String(value);
}

function csvDiffPct(value: number | null): string {
    if (value === null) {
        return "";
    }
    return niceNumber(value, 2, 2);
}

export type ModelsStagingCsvHeaders = {
    plantExternalId: string;
    lineExternalId: string;
    materialExternalId: string;
    process: string;
    activeSlope: string;
    pendingSlope: string;
    normSlope: string;
    oldVsNewDiffPct: string;
    oldVsNormDiffPct: string;
};

export function buildModelsStagingCsvRows(rows: IModelComparisonRow[], headers: ModelsStagingCsvHeaders): string[][] {
    const headerRow = [
        headers.plantExternalId,
        headers.lineExternalId,
        headers.materialExternalId,
        headers.process,
        headers.activeSlope,
        headers.pendingSlope,
        headers.normSlope,
        headers.oldVsNewDiffPct,
        headers.oldVsNormDiffPct,
    ];

    const dataRows = rows.map(row => [
        row.plant_external_id,
        row.line_external_id,
        row.material_external_id,
        row.process_num,
        csvNumber(row.active_slope),
        csvNumber(row.pending_slope),
        csvNumber(row.norm_slope),
        csvDiffPct(row.old_vs_new_diff_pct),
        csvDiffPct(row.old_vs_norm_diff_pct),
    ]);

    return [headerRow, ...dataRows];
}

export async function fetchModelsComparisonForExport(
    fetchPage: (req: IGetModelsComparisonReq) => Promise<{ rows: IModelComparisonRow[] }>,
    baseReq: IGetModelsComparisonReq
): Promise<IModelComparisonRow[]> {
    const res = await fetchPage({
        ...baseReq,
        for_export: true,
        page: 1,
        page_size: 1,
    });
    return res.rows;
}
